import React from "react";
import { Link } from "react-router-dom";
import { Cpu, Mail, Phone, MapPin, Clock, Facebook, MessageCircle } from "lucide-react";
import { BUSINESS } from "@/lib/business";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-slate-200 bg-white mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white">
              <Cpu className="w-5 h-5" strokeWidth={2.2} />
            </span>
            <span className="font-display text-lg font-bold tracking-tight">Mymi<span className="text-blue-600">·</span>Global Business</span>
          </Link>
          <p className="mt-4 text-sm text-slate-600 max-w-md leading-relaxed">
            A curated selection of tested, inspected, and warrantied refurbished laptops, desktops,
            monitors, and business equipment — sold at fair prices to individuals and organizations.
          </p>
          <div className="mt-5 flex items-center gap-2">
            <a
              href={BUSINESS.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook"
              className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-blue-600 hover:text-white grid place-items-center text-slate-700 transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={BUSINESS.social.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              aria-label="WhatsApp"
              className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-emerald-500 hover:text-white grid place-items-center text-slate-700 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">Shop</div>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li><Link to="/inventory" className="hover:text-blue-600">All inventory</Link></li>
            <li><Link to="/inventory?category=Laptops" className="hover:text-blue-600">Laptops</Link></li>
            <li><Link to="/inventory?category=Desktop Computers" className="hover:text-blue-600">Desktops</Link></li>
            <li><Link to="/inventory?category=Monitors" className="hover:text-blue-600">Monitors</Link></li>
            <li><Link to="/inventory?category=Computer Accessories" className="hover:text-blue-600">Accessories</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">Contact</div>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <a href={`mailto:${BUSINESS.email}`} className="hover:text-blue-600 truncate" data-testid="footer-email">{BUSINESS.email}</a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <a href={`tel:${BUSINESS.phoneRaw}`} className="hover:text-blue-600" data-testid="footer-phone">{BUSINESS.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <span data-testid="footer-address">{BUSINESS.address}</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <span data-testid="footer-hours">{BUSINESS.hours}</span>
            </li>
          </ul>
          <Link
            to="/contact"
            className="inline-flex mt-4 items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            data-testid="footer-contact-cta"
          >
            Send a message →
          </Link>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Mymi Global Business. All rights reserved.</span>
          <span className="font-mono">Trusted refurbished technology · Tested & inspected</span>
        </div>
      </div>
    </footer>
  );
}
